import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUptakeList } from 'modules/domain/uptake/hooks'
import { useSelector } from 'react-redux'
import UptakeSelectors from 'modules/domain/uptake/selectors'
import UptakeActions from 'modules/domain/uptake/duck'
import { UptakeStatus, UptakeListRequestFilter } from 'modules/domain/uptake/types'
import * as TComponents from 'views/components/CommonTableComponents/CommonTableComponents'
import { CellProps, useTable } from 'react-table'
import { Uptake } from 'modules/domain/uptake/types'
import {
  Button,
  Flag,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableNoData,
  useAction,
  useHistoryPush,
  usePersistentScroll,
} from '@agro-club/frontend-shared'
import Status from 'views/pages/CallBackRequest/Status'
import UpTakeRoutes from 'views/pages/UpTake/routes'
import { TableFilter } from 'views/components/TableFilters/TableFilters'
import useDateFormatFn from 'hooks/useDateFormatFn'

const RecordActions: React.FC<CellProps<Uptake>> = ({ row, column }) => {
  const { t } = useTranslation('uptake')
  const approveAction = useAction(UptakeActions.updateRequested, row.values.id, {
    status: UptakeStatus.DONE,
  })
  const meta = useSelector((state) => UptakeSelectors.meta(state, row.values.id))
  if (row.values.status === UptakeStatus.DONE) {
    return null
  }

  return (
    <div key={column.id}>
      <Button
        size={'small'}
        progress={meta.updateProgress}
        intent={'action'}
        filled={false}
        onClick={() => approveAction()}
      >
        {t('approve')}
      </Button>
    </div>
  )
}

const RegionCell: React.FC<CellProps<Uptake>> = ({ row, column }) => {
  if (!row.values.region) {
    return null
  }

  return (
    <div key={column.id}>
      <Flag code={row.values.region} />
    </div>
  )
}

const CreatedAtCell: React.FC<CellProps<Uptake>> = ({ row, column }) => {
  const getFormatedDate = useDateFormatFn({ withTime: true })

  return (
    <div key={column.id}>
      <div>{getFormatedDate(row.values.created_at)}</div>
    </div>
  )
}

const UpdatedAtCell: React.FC<CellProps<Uptake>> = ({ row, column }) => {
  const getFormatedDate = useDateFormatFn({ withTime: true })

  return (
    <div key={column.id}>
      <div>{getFormatedDate(row.values.created_at)}</div>
    </div>
  )
}

const StatusCell: React.FC<CellProps<Uptake>> = ({ row, column }) => {
  return (
    <div key={column.id}>
      <Status status={row.values.status} />
    </div>
  )
}

const UpTakeTable: React.FC = () => {
  const push = useHistoryPush()
  const { t } = useTranslation(['uptake', 'labels'])
  const [progress, data = []] = useUptakeList()
  const total = useSelector(UptakeSelectors.total)
  const page = useSelector(UptakeSelectors.page)
  const pages = useSelector(UptakeSelectors.pages)
  const pageSize = useSelector(UptakeSelectors.pageSize)
  const filter = useSelector(UptakeSelectors.filter)

  const listRequested = useAction(UptakeActions.listRequested)
  const filterUpdated = useAction(UptakeActions.filterUpdated)

  const { scrollRef } = usePersistentScroll('upTakeTable')

  const statusFilterOptions = useMemo(
    () => [
      {
        value: UptakeStatus.NEW,
        title: t('status.new'),
        bullet: true,
        color: 'orange' as const,
      },
      {
        value: UptakeStatus.DONE,
        title: t('status.done'),
        bullet: true,
        color: 'green' as const,
      },
    ],
    [t],
  )

  const allColumns = useMemo(
    () => [
      {
        Header: t('fields.actions'),
        accessor: 'id' as const,
        sortable: false,
        Cell: RecordActions,
      },
      {
        Header: t('fields.status'),
        accessor: 'status' as const,
        sortable: false,
        Cell: StatusCell,
      },
      {
        Header: t('fields.region'),
        accessor: 'region' as const,
        sortable: false,
        Cell: RegionCell,
      },
      {
        Header: t('fields.contact'),
        accessor: 'email_or_phone' as const,
        sortable: false,
      },
      {
        Header: t('fields.message'),
        accessor: 'message' as const,
        sortable: false,
      },
      {
        Header: t('fields.createdAt'),
        accessor: 'created_at' as const,
        sortable: false,
        Cell: CreatedAtCell,
      },
      {
        Header: t('fields.createdAt'),
        accessor: 'updated_at' as const,
        sortable: false,
        Cell: UpdatedAtCell,
      },
    ],
    [t],
  )

  const { columns, rows, prepareRow } = useTable<Uptake>({
    columns: allColumns,
    data,
  })

  const fetchNextItems = useCallback(
    (num) => {
      listRequested({ page: num })
    },
    [listRequested],
  )

  const handleFilterChange = useCallback(
    (newValue: Partial<UptakeListRequestFilter>) => {
      filterUpdated({ ...filter, ...newValue })
    },
    [filter, filterUpdated],
  )

  return (
    <TComponents.Wrapper>
      <TComponents.Filters>
        <TableFilter
          handleFilterChange={handleFilterChange}
          title={t('labels:status')}
          filterValue={{ status: filter.status }}
          options={statusFilterOptions}
          disableMulti
        />
      </TComponents.Filters>
      <Table
        total={total}
        pages={pages}
        pageSize={pageSize}
        currentPage={page}
        onSetPage={fetchNextItems}
        ref={scrollRef}
      >
        <TableHead>
          <TableHeadRow>
            {columns.map((column) => {
              return <TableHeadCell key={column.id}>{column.render('Header')}</TableHeadCell>
            })}
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...props } = row.getRowProps()
            return (
              <TableBodyRow
                key={key}
                {...props}
                onClick={() => push({ route: UpTakeRoutes.Item, params: { id: row.values.id } })}
              >
                {row.cells.map((cell) => {
                  const { key, ...props } = cell.getCellProps()
                  return (
                    <TableBodyCell key={key} {...props}>
                      {cell.render('Cell')}
                    </TableBodyCell>
                  )
                })}
              </TableBodyRow>
            )
          })}
          <TableNoData
            progress={progress}
            isEmpty={!rows.length}
            colSpan={allColumns.length}
            loading={<TComponents.Spinner />}
          />
        </TableBody>
      </Table>
    </TComponents.Wrapper>
  )
}

export default UpTakeTable
